import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Spinner, Alert, Badge, Modal, Table } from 'react-bootstrap';
import axiosInstance from '../../utils/setupAxios';

const ConfigSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({ key: '', label: '', value: '' });

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Function to group configs by first word before underscore
  const groupConfigsByPrefix = (configs) => {
    const groups = {};
    
    configs.forEach(config => {
      const key = config.key;
      const prefix = key.split('_')[0]; // Get first word before underscore
      const capitalizedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1); // Capitalize first letter
      
      if (!groups[capitalizedPrefix]) {
        groups[capitalizedPrefix] = [];
      }
      groups[capitalizedPrefix].push(config);
    });
    
    return groups;
  };

  const fetchConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get('/songs/settings/api-config/list');
      if (res.data.success) {
        setConfigs(res.data.data || []);
      } else {
        setError(res.data.message || 'Failed to load configs');
      }
    } catch (err) {
      setError('Failed to load configs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingConfig(null);
    setFormData({ key: '', label: '', value: '' });
    setShowModal(true);
    setMessage('');
    setError('');
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({ key: config.key, label: config.label, value: config.value });
    setShowModal(true);
    setMessage('');
    setError('');
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Are you sure you want to delete this config?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.post('/songs/settings/api-config/delete', { key });
      if (res.data.success) {
        setMessage('Config deleted successfully');
        fetchConfigs();
      } else {
        setError(res.data.message || 'Failed to delete config');
      }
    } catch (err) {
      setError('Failed to delete config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.key || !formData.label) {
      setError('Key and Label are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      let res;
      
      if (editingConfig) {
        // Update existing config
        const formDataToSend = new FormData();
        formDataToSend.append('key', formData.key);
        formDataToSend.append('value', formData.value);
        formDataToSend.append('label', formData.label);
        
        res = await axiosInstance.post('/songs/settings/api-config/update', formDataToSend);
      } else {
        // Add new config
        const formDataToSend = new FormData();
        formDataToSend.append('key', formData.key);
        formDataToSend.append('value', formData.value);
        formDataToSend.append('label', formData.label);
        
        res = await axiosInstance.post('/songs/settings/api-config/add', formDataToSend);
      }
      
      if (res.data.success) {
        setMessage(editingConfig ? 'Config updated successfully' : 'Config added successfully');
        setShowModal(false);
        fetchConfigs();
      } else {
        setError(res.data.message || 'Failed to save config');
      }
    } catch (err) {
      setError('Failed to save config');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setFormData({ key: '', label: '', value: '' });
    setMessage('');
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Group configs by prefix
  const groupedConfigs = groupConfigsByPrefix(configs);

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">API Configuration</h2>
          <Button variant="primary" onClick={handleAdd} disabled={loading}>
            Add New Config
          </Button>
        </div>
        
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        {message && <Alert variant="success" className="mb-3">{message}</Alert>}
        
        {loading && !configs.length ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <div>
            {Object.keys(groupedConfigs).length === 0 ? (
              <div className="text-center py-4 text-muted">
                No configuration found. Click "Add New Config" to create one.
              </div>
            ) : (
              Object.entries(groupedConfigs).map(([groupName, groupConfigs]) => (
                <div key={groupName} className="mb-4">
                  <div className="mb-3">
                    <Badge bg="primary" className="fs-5 px-3 py-2 text-white">{groupName}</Badge>
                  </div>
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Label</th>
                        <th>Value</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupConfigs.map((config, index) => (
                        <tr key={index}>
                          <td>
                            <Badge bg="info" className="text-dark">{config.key}</Badge>
                          </td>
                          <td>{config.label}</td>
                          <td>
                            <code className="bg-light px-2 py-1 rounded">{config.value}</code>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEdit(config)}
                              disabled={loading}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(config.key)}
                              disabled={loading}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ))
            )}
          </div>
        )}

        <Modal show={showModal} onHide={handleCloseModal} centered scrollable fullscreen="sm-down">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingConfig ? 'Edit Configuration' : 'Add New Configuration'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="config-key">
                <Form.Label>Key *</Form.Label>
                <Form.Control
                  type="text"
                  name="key"
                  value={formData.key}
                  onChange={handleInputChange}
                  disabled={loading || editingConfig}
                  placeholder="e.g. suno_model"
                />
                <Form.Text className="text-muted">
                  Unique identifier for the configuration
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="config-label">
                <Form.Label>Label *</Form.Label>
                <Form.Control
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="e.g. Suno Model"
                />
                <Form.Text className="text-muted">
                  Display name for the configuration
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="config-value">
                <Form.Label>Value</Form.Label>
                <Form.Control
                  type="text"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="e.g. V4_5"
                />
                <Form.Text className="text-muted">
                  Configuration value
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default ConfigSettings; 